import React, { useState, useEffect } from 'react';
import { 
  Target, 
  TrendingUp, 
  Users, 
  Lightbulb, 
  Activity,
  BarChart3,
  Clock,
  CheckCircle,
  AlertTriangle,
  Zap,
  Brain,
  Star,
  ArrowUp,
  ArrowDown,
  Eye,
  X,
  Filter,
  Search,
  Sliders,
  Play,
  PlusCircle,
  MinusCircle,
  Calendar,
  DollarSign,
  Shield,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Download,
  Share2
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ScatterChart, Scatter } from 'recharts';

// Define interfaces for our data types
interface StrategicMetric {
  name: string;
  current: number;
  target: number;
  forecast: number;
  trend: 'up' | 'down' | 'stable';
  color: string;
  theme?: string;
}

interface BacklogItem {
  id: number;
  name: string;
  priority: number;
  effort: number;
  impact: 'High' | 'Medium' | 'Low';
  confidence: number;
  status: 'In Progress' | 'Ready' | 'Discovery' | 'Backlog' | 'Completed';
  owner: string;
  dependencies: string[];
  risks: string[];
  timeline: string;
  businessValue: number;
  riskScore: number;
  strategicAlignment: number;
  learningVelocity: number;
  cycleTime: number;
  kpiImpact: string[];
  theme: string;
  tags: string[];
  startDate?: string;
  endDate?: string;
  progress?: number;
}

interface ProductInsight {
  id: number;
  type: 'opportunity' | 'concern' | 'success';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  confidence: number;
  action: string;
  timeframe: string;
  theme?: string;
  tags?: string[];
  relatedItems?: number[];
}

interface SimulationScenario {
  id: number;
  name: string;
  description: string;
  parameters: SimulationParameter[];
  results: SimulationResult[];
}

interface SimulationParameter {
  name: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  description: string;
}

interface SimulationResult {
  metric: string;
  baseline: number;
  simulated: number;
  change: number;
  unit: string;
}

const ProductDashboard: React.FC = () => {
  // State management
  const [selectedTimeframe, setSelectedTimeframe] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTheme, setSelectedTheme] = useState<string>('all');
  const [selectedRisk, setSelectedRisk] = useState<string>('all');
  const [selectedImpact, setSelectedImpact] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSimulatorOpen, setIsSimulatorOpen] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState<ProductInsight | null>(null);
  const [currentScenario, setCurrentScenario] = useState<SimulationScenario | null>(null);
  const [simulationParameters, setSimulationParameters] = useState<SimulationParameter[]>([]);

  // Strategic alignment data over time (12 months)
  const strategicAlignmentData = [
    { month: 'Jan', customerAcquisition: 72, revenueGrowth: 68, userRetention: 85, marketExpansion: 45, operationalEfficiency: 78 },
    { month: 'Feb', customerAcquisition: 75, revenueGrowth: 71, userRetention: 87, marketExpansion: 48, operationalEfficiency: 80 },
    { month: 'Mar', customerAcquisition: 78, revenueGrowth: 74, userRetention: 89, marketExpansion: 52, operationalEfficiency: 82 },
    { month: 'Apr', customerAcquisition: 80, revenueGrowth: 76, userRetention: 88, marketExpansion: 55, operationalEfficiency: 84 },
    { month: 'May', customerAcquisition: 82, revenueGrowth: 79, userRetention: 90, marketExpansion: 58, operationalEfficiency: 86 },
    { month: 'Jun', customerAcquisition: 85, revenueGrowth: 82, userRetention: 92, marketExpansion: 62, operationalEfficiency: 88 },
    { month: 'Jul', customerAcquisition: 87, revenueGrowth: 84, userRetention: 91, marketExpansion: 65, operationalEfficiency: 89 },
    { month: 'Aug', customerAcquisition: 89, revenueGrowth: 86, userRetention: 93, marketExpansion: 68, operationalEfficiency: 91 },
    { month: 'Sep', customerAcquisition: 91, revenueGrowth: 88, userRetention: 94, marketExpansion: 70, operationalEfficiency: 92 },
    { month: 'Oct', customerAcquisition: 88, revenueGrowth: 85, userRetention: 92, marketExpansion: 72, operationalEfficiency: 90 },
    { month: 'Nov', customerAcquisition: 90, revenueGrowth: 87, userRetention: 95, marketExpansion: 75, operationalEfficiency: 93 },
    { month: 'Dec', customerAcquisition: 92, revenueGrowth: 89, userRetention: 96, marketExpansion: 78, operationalEfficiency: 95 },
  ];

  // Current strategic metrics with horizontal layout data
  const strategicMetrics: StrategicMetric[] = [
    {
      name: 'Customer Acquisition',
      current: 92,
      target: 90,
      forecast: 94,
      trend: 'up',
      color: '#3B82F6',
      theme: 'Growth'
    },
    {
      name: 'Revenue Growth',
      current: 89,
      target: 85,
      forecast: 91,
      trend: 'up',
      color: '#10B981',
      theme: 'Growth'
    },
    {
      name: 'User Retention',
      current: 96,
      target: 95,
      forecast: 97,
      trend: 'up',
      color: '#8B5CF6',
      theme: 'Engagement'
    },
    {
      name: 'Market Expansion',
      current: 78,
      target: 80,
      forecast: 82,
      trend: 'up',
      color: '#F59E0B',
      theme: 'Growth'
    },
    {
      name: 'Operational Efficiency',
      current: 95,
      target: 90,
      forecast: 96,
      trend: 'up',
      color: '#EF4444',
      theme: 'Efficiency'
    }
  ];

  // Product-specific metrics data
  const learningVelocityData = [
    { week: 'W1', velocity: 65, discoveries: 3, validations: 8 },
    { week: 'W2', velocity: 72, discoveries: 5, validations: 12 },
    { week: 'W3', velocity: 68, discoveries: 4, validations: 10 },
    { week: 'W4', velocity: 85, discoveries: 7, validations: 15 },
    { week: 'W5', velocity: 92, discoveries: 8, validations: 18 },
    { week: 'W6', velocity: 88, discoveries: 6, validations: 16 },
  ];

  const behaviorChangeData = [
    { segment: 'New Users', adoption: 78, retention: 65, satisfaction: 82 },
    { segment: 'Power Users', adoption: 92, retention: 88, satisfaction: 91 },
    { segment: 'Enterprise', adoption: 85, retention: 79, satisfaction: 87 },
    { segment: 'SMB', adoption: 71, retention: 68, satisfaction: 76 },
  ];

  const featureAdoptionData = [
    { feature: 'AI Personalisation', adoption: 68, impact: 'high', users: 12400 },
    { feature: 'Voice Interface', adoption: 34, impact: 'medium', users: 5800 },
    { feature: 'Mobile Checkout', adoption: 89, impact: 'high', users: 18900 },
    { feature: 'Social Sharing', adoption: 23, impact: 'low', users: 3200 },
    { feature: 'Advanced Analytics', adoption: 45, impact: 'medium', users: 7600 },
  ];

  const hypothesesPipelineData = [
    { stage: 'Ideation', count: 24, color: '#3B82F6' },
    { stage: 'Validation', count: 12, color: '#8B5CF6' },
    { stage: 'Experiment Running', count: 8, color: '#F59E0B' },
    { stage: 'Validated', count: 15, color: '#10B981' },
    { stage: 'Rejected', count: 6, color: '#EF4444' },
  ];

  // Enhanced prioritization data with additional fields for backlog health
  const prioritizationFeatures: BacklogItem[] = [
    {
      id: 1,
      name: 'AI-Powered Personalisation Engine',
      priority: 95,
      effort: 8,
      impact: 'High',
      confidence: 92,
      status: 'In Progress',
      owner: 'Sarah Chen',
      dependencies: ['Data Pipeline', 'ML Infrastructure'],
      risks: ['Data Quality', 'Model Accuracy'],
      timeline: '6 weeks',
      businessValue: 95,
      riskScore: 30,
      strategicAlignment: 98,
      learningVelocity: 92,
      cycleTime: 14,
      kpiImpact: ['Customer Satisfaction', 'Revenue Growth', 'Market Share', 'Learning Velocity', 'Innovation Index'],
      theme: 'AI & Personalisation',
      tags: ['AI', 'ML', 'Personalisation', 'Customer Experience'],
      startDate: '2025-06-01',
      endDate: '2025-07-15',
      progress: 45
    },
    {
      id: 2,
      name: 'Mobile Checkout Optimization',
      priority: 88,
      effort: 5,
      impact: 'High',
      confidence: 89,
      status: 'Ready',
      owner: 'Mike Rodriguez',
      dependencies: ['Payment Gateway'],
      risks: ['Browser Compatibility'],
      timeline: '3 weeks',
      businessValue: 85,
      riskScore: 15,
      strategicAlignment: 89,
      learningVelocity: 78,
      cycleTime: 8,
      kpiImpact: ['Conversion Rate', 'Customer Satisfaction', 'Mobile Revenue', 'User Experience Score'],
      theme: 'Mobile Experience',
      tags: ['Mobile', 'UX', 'Checkout', 'Conversion'],
      startDate: '2025-06-15',
      endDate: '2025-07-05',
      progress: 0
    },
    {
      id: 3,
      name: 'Voice Interface Integration',
      priority: 76,
      effort: 12,
      impact: 'Medium',
      confidence: 71,
      status: 'Discovery',
      owner: 'Emma Watson',
      dependencies: ['Voice API', 'UX Research'],
      risks: ['User Adoption', 'Technical Complexity'],
      timeline: '10 weeks',
      businessValue: 70,
      riskScore: 45,
      strategicAlignment: 75,
      learningVelocity: 85,
      cycleTime: 21,
      kpiImpact: ['Innovation Index', 'Accessibility Score', 'Customer Satisfaction', 'Market Differentiation'],
      theme: 'Accessibility',
      tags: ['Voice', 'Accessibility', 'Innovation', 'UX'],
      startDate: '2025-07-01',
      endDate: '2025-09-15',
      progress: 15
    },
    {
      id: 4,
      name: 'Advanced Analytics Dashboard',
      priority: 65,
      effort: 6,
      impact: 'Medium',
      confidence: 78,
      status: 'Backlog',
      owner: 'David Kumar',
      dependencies: ['Data Warehouse'],
      risks: ['Performance'],
      timeline: '4 weeks',
      businessValue: 75,
      riskScore: 20,
      strategicAlignment: 86,
      learningVelocity: 88,
      cycleTime: 12,
      kpiImpact: ['Customer Satisfaction', 'Learning Velocity', 'Product Quality', 'Behaviour Change Rate'],
      theme: 'Data & Analytics',
      tags: ['Analytics', 'Dashboard', 'Data', 'Insights'],
      startDate: '2025-08-01',
      endDate: '2025-08-30',
      progress: 0
    },
    {
      id: 5,
      name: 'Subscription Tier Pricing',
      priority: 82,
      effort: 4,
      impact: 'High',
      confidence: 85,
      status: 'Ready',
      owner: 'Lisa Park',
      dependencies: ['Billing System', 'Marketing Assets'],
      risks: ['Market Acceptance', 'Pricing Strategy'],
      timeline: '3 weeks',
      businessValue: 90,
      riskScore: 25,
      strategicAlignment: 92,
      learningVelocity: 75,
      cycleTime: 10,
      kpiImpact: ['Revenue Growth', 'Customer Acquisition', 'Lifetime Value', 'Conversion Rate'],
      theme: 'Monetisation',
      tags: ['Pricing', 'Subscription', 'Revenue', 'Business Model'],
      startDate: '2025-06-20',
      endDate: '2025-07-10',
      progress: 0
    },
    {
      id: 6,
      name: 'Security Compliance Upgrade',
      priority: 78,
      effort: 7,
      impact: 'Medium',
      confidence: 95,
      status: 'Backlog',
      owner: 'David Kumar',
      dependencies: ['Security Audit', 'Legal Review'],
      risks: ['Regulatory Changes', 'Implementation Complexity'],
      timeline: '5 weeks',
      businessValue: 60,
      riskScore: 85,
      strategicAlignment: 82,
      learningVelocity: 70,
      cycleTime: 15,
      kpiImpact: ['Security Score', 'Compliance Rating', 'Risk Mitigation', 'Trust Index'],
      theme: 'Security & Compliance',
      tags: ['Security', 'Compliance', 'Risk', 'Governance'],
      startDate: '2025-07-15',
      endDate: '2025-08-20',
      progress: 0
    },
    {
      id: 7,
      name: 'Social Integration Features',
      priority: 62,
      effort: 5,
      impact: 'Medium',
      confidence: 68,
      status: 'Backlog',
      owner: 'Mike Rodriguez',
      dependencies: ['Social APIs', 'UX Design'],
      risks: ['API Changes', 'User Privacy'],
      timeline: '4 weeks',
      businessValue: 65,
      riskScore: 30,
      strategicAlignment: 70,
      learningVelocity: 80,
      cycleTime: 12,
      kpiImpact: ['User Engagement', 'Viral Coefficient', 'Acquisition Cost', 'Brand Awareness'],
      theme: 'User Engagement',
      tags: ['Social', 'Sharing', 'Engagement', 'Growth'],
      startDate: '2025-08-15',
      endDate: '2025-09-15',
      progress: 0
    },
    {
      id: 8,
      name: 'Performance Optimization',
      priority: 85,
      effort: 6,
      impact: 'High',
      confidence: 88,
      status: 'In Progress',
      owner: 'David Kumar',
      dependencies: ['Monitoring Tools', 'Infrastructure'],
      risks: ['Technical Debt', 'Regression'],
      timeline: '5 weeks',
      businessValue: 80,
      riskScore: 20,
      strategicAlignment: 85,
      learningVelocity: 75,
      cycleTime: 14,
      kpiImpact: ['Page Load Time', 'User Satisfaction', 'Bounce Rate', 'Conversion Rate'],
      theme: 'Technical Excellence',
      tags: ['Performance', 'Speed', 'Optimization', 'Technical'],
      startDate: '2025-06-10',
      endDate: '2025-07-15',
      progress: 30
    }
  ];

  // Key product metrics
  const productMetrics = [
    {
      id: 'learning_velocity',
      name: 'Learning Velocity',
      value: 88,
      change: 12.5,
      positive: true,
      description: 'Rate of validated learnings and unexpected discoveries',
      target: 85,
      unit: '%'
    },
    {
      id: 'customer_satisfaction',
      name: 'Customer Satisfaction',
      value: 4.7,
      change: 0.3,
      positive: true,
      description: 'Overall customer satisfaction score',
      target: 4.5,
      unit: '/5'
    },
    {
      id: 'feature_adoption',
      name: 'Feature Adoption Rate',
      value: 72,
      change: -2.1,
      positive: false,
      description: 'Average adoption rate of new features',
      target: 75,
      unit: '%'
    },
    {
      id: 'innovation_index',
      name: 'Innovation Index',
      value: 82,
      change: 8.7,
      positive: true,
      description: 'Innovation pipeline health and velocity',
      target: 80,
      unit: '%'
    }
  ];

  // Enhanced product insights with themes and tags
  const productInsights: ProductInsight[] = [
    {
      id: 1,
      type: 'opportunity',
      title: 'AI Personalisation Breakthrough',
      description: 'User engagement increased 34% with new ML model',
      impact: 'high',
      confidence: 92,
      action: 'Scale to all user segments',
      timeframe: 'This week',
      theme: 'AI & Personalisation',
      tags: ['AI', 'ML', 'Engagement', 'Personalisation'],
      relatedItems: [1]
    },
    {
      id: 2,
      type: 'concern',
      title: 'Feature Adoption Plateau',
      description: 'New feature adoption has slowed in enterprise segment',
      impact: 'medium',
      confidence: 78,
      action: 'Investigate user feedback and barriers',
      timeframe: '2 weeks ago',
      theme: 'User Engagement',
      tags: ['Adoption', 'Enterprise', 'User Research'],
      relatedItems: [3, 7]
    },
    {
      id: 3,
      type: 'success',
      title: 'Mobile Experience Wins',
      description: 'Mobile conversion rate improved 28% after redesign',
      impact: 'high',
      confidence: 95,
      action: 'Apply learnings to desktop experience',
      timeframe: '1 week ago',
      theme: 'Mobile Experience',
      tags: ['Mobile', 'Conversion', 'UX', 'Success'],
      relatedItems: [2]
    },
    {
      id: 4,
      type: 'opportunity',
      title: 'Subscription Model Optimization',
      description: 'Analysis shows potential 15% revenue increase with tiered pricing',
      impact: 'high',
      confidence: 85,
      action: 'Implement A/B test for new pricing tiers',
      timeframe: '3 days ago',
      theme: 'Monetisation',
      tags: ['Pricing', 'Revenue', 'Subscription', 'Growth'],
      relatedItems: [5]
    },
    {
      id: 5,
      type: 'concern',
      title: 'Security Compliance Gap',
      description: 'Recent audit identified potential compliance issues in data handling',
      impact: 'high',
      confidence: 90,
      action: 'Prioritize security compliance upgrade',
      timeframe: 'Yesterday',
      theme: 'Security & Compliance',
      tags: ['Security', 'Compliance', 'Risk', 'Regulatory'],
      relatedItems: [6]
    }
  ];

  // Simulation scenarios
  const simulationScenarios: SimulationScenario[] = [
    {
      id: 1,
      name: 'Resource Reallocation',
      description: 'Simulate the impact of reallocating resources between features',
      parameters: [
        {
          name: 'AI Personalisation Resources',
          value: 100,
          min: 50,
          max: 150,
          step: 10,
          unit: '%',
          description: 'Percentage of current resources allocated to AI Personalisation'
        },
        {
          name: 'Mobile Checkout Resources',
          value: 100,
          min: 50,
          max: 150,
          step: 10,
          unit: '%',
          description: 'Percentage of current resources allocated to Mobile Checkout'
        },
        {
          name: 'Voice Interface Resources',
          value: 100,
          min: 50,
          max: 150,
          step: 10,
          unit: '%',
          description: 'Percentage of current resources allocated to Voice Interface'
        }
      ],
      results: [
        {
          metric: 'Overall Delivery Timeline',
          baseline: 12,
          simulated: 12,
          change: 0,
          unit: 'weeks'
        },
        {
          metric: 'Strategic Alignment Score',
          baseline: 85,
          simulated: 85,
          change: 0,
          unit: '%'
        },
        {
          metric: 'Business Value',
          baseline: 82,
          simulated: 82,
          change: 0,
          unit: '%'
        },
        {
          metric: 'Risk Score',
          baseline: 35,
          simulated: 35,
          change: 0,
          unit: '%'
        }
      ]
    },
    {
      id: 2,
      name: 'Market Condition Changes',
      description: 'Simulate the impact of changing market conditions on priorities',
      parameters: [
        {
          name: 'Competitive Pressure',
          value: 50,
          min: 0,
          max: 100,
          step: 5,
          unit: '%',
          description: 'Level of competitive pressure in the market'
        },
        {
          name: 'Customer Demand',
          value: 50,
          min: 0,
          max: 100,
          step: 5,
          unit: '%',
          description: 'Level of customer demand for features'
        },
        {
          name: 'Regulatory Changes',
          value: 50,
          min: 0,
          max: 100,
          step: 5,
          unit: '%',
          description: 'Severity of regulatory changes'
        }
      ],
      results: [
        {
          metric: 'Priority Shift',
          baseline: 0,
          simulated: 0,
          change: 0,
          unit: '%'
        },
        {
          metric: 'Risk Exposure',
          baseline: 35,
          simulated: 35,
          change: 0,
          unit: '%'
        },
        {
          metric: 'Market Opportunity',
          baseline: 75,
          simulated: 75,
          change: 0,
          unit: '%'
        },
        {
          metric: 'Strategic Alignment',
          baseline: 85,
          simulated: 85,
          change: 0,
          unit: '%'
        }
      ]
    },
    {
      id: 3,
      name: 'Timeline Adjustments',
      description: 'Simulate the impact of adjusting delivery timelines',
      parameters: [
        {
          name: 'AI Personalisation Timeline',
          value: 6,
          min: 4,
          max: 10,
          step: 0.5,
          unit: 'weeks',
          description: 'Timeline for AI Personalisation feature'
        },
        {
          name: 'Mobile Checkout Timeline',
          value: 3,
          min: 2,
          max: 6,
          step: 0.5,
          unit: 'weeks',
          description: 'Timeline for Mobile Checkout feature'
        },
        {
          name: 'Voice Interface Timeline',
          value: 10,
          min: 6,
          max: 14,
          step: 0.5,
          unit: 'weeks',
          description: 'Timeline for Voice Interface feature'
        }
      ],
      results: [
        {
          metric: 'Resource Utilization',
          baseline: 85,
          simulated: 85,
          change: 0,
          unit: '%'
        },
        {
          metric: 'Quality Impact',
          baseline: 90,
          simulated: 90,
          change: 0,
          unit: '%'
        },
        {
          metric: 'Team Burnout Risk',
          baseline: 25,
          simulated: 25,
          change: 0,
          unit: '%'
        },
        {
          metric: 'Business Value Delivery',
          baseline: 82,
          simulated: 82,
          change: 0,
          unit: '%'
        }
      ]
    }
  ];

  // Available strategic themes for filtering
  const strategicThemes = [
    'All Themes',
    'AI & Personalisation',
    'Mobile Experience',
    'Accessibility',
    'Data & Analytics',
    'Monetisation',
    'Security & Compliance',
    'User Engagement',
    'Technical Excellence',
    'Growth',
    'Efficiency',
    'Engagement'
  ];

  // Available risk areas for filtering
  const riskAreas = [
    'All Risks',
    'Data Quality',
    'Model Accuracy',
    'Browser Compatibility',
    'User Adoption',
    'Technical Complexity',
    'Performance',
    'Market Acceptance',
    'Pricing Strategy',
    'Regulatory Changes',
    'Implementation Complexity',
    'API Changes',
    'User Privacy',
    'Technical Debt',
    'Regression'
  ];

  // Available impact areas for filtering
  const impactAreas = [
    'All Impact Areas',
    'Customer Satisfaction',
    'Revenue Growth',
    'Market Share',
    'Learning Velocity',
    'Innovation Index',
    'Conversion Rate',
    'Mobile Revenue',
    'User Experience Score',
    'Accessibility Score',
    'Market Differentiation',
    'Product Quality',
    'Behaviour Change Rate',
    'Security Score',
    'Compliance Rating',
    'Risk Mitigation',
    'Trust Index'
  ];

  // Effect to initialize simulation parameters when a scenario is selected
  useEffect(() => {
    if (currentScenario) {
      setSimulationParameters([...currentScenario.parameters]);
    }
  }, [currentScenario]);

  // Function to update simulation parameter
  const updateParameter = (index: number, value: number) => {
    if (!currentScenario) return;
    
    const updatedParams = [...simulationParameters];
    updatedParams[index].value = value;
    setSimulationParameters(updatedParams);
    
    // In a real implementation, this would trigger a recalculation of the simulation results
    // For now, we'll just simulate a change in the results
    const updatedScenario = {...currentScenario};
    updatedScenario.results = currentScenario.results.map(result => {
      // Simulate some impact on the results based on parameter changes
      const changePercent = (Math.random() * 20) - 10; // Random change between -10% and +10%
      const newSimulated = result.baseline * (1 + (changePercent / 100));
      return {
        ...result,
        simulated: parseFloat(newSimulated.toFixed(1)),
        change: parseFloat((((newSimulated / result.baseline) - 1) * 100).toFixed(1))
      };
    });
    
    setCurrentScenario(updatedScenario);
  };

  // Function to run simulation
  const runSimulation = () => {
    if (!currentScenario) return;
    
    // In a real implementation, this would trigger a more complex simulation
    // For now, we'll just simulate more significant changes in the results
    const updatedScenario = {...currentScenario};
    updatedScenario.results = currentScenario.results.map(result => {
      // More significant changes for "Run Simulation"
      const changePercent = (Math.random() * 40) - 20; // Random change between -20% and +20%
      const newSimulated = result.baseline * (1 + (changePercent / 100));
      return {
        ...result,
        simulated: parseFloat(newSimulated.toFixed(1)),
        change: parseFloat((((newSimulated / result.baseline) - 1) * 100).toFixed(1))
      };
    });
    
    setCurrentScenario(updatedScenario);
  };

  // Filter prioritization features based on selected filters
  const filteredPrioritizationFeatures = prioritizationFeatures.filter(feature => {
    const matchesTheme = selectedTheme === 'All Themes' || feature.theme === selectedTheme;
    const matchesRisk = selectedRisk === 'All Risks' || feature.risks.some(risk => risk.includes(selectedRisk.replace('All Risks', '')));
    const matchesImpact = selectedImpact === 'All Impact Areas' || feature.kpiImpact.some(kpi => kpi.includes(selectedImpact.replace('All Impact Areas', '')));
    const matchesStatus = selectedStatus === 'all' || feature.status.toLowerCase().includes(selectedStatus.toLowerCase());
    const matchesSearch = searchTerm === '' || 
                         feature.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         feature.owner.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         feature.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesTheme && matchesRisk && matchesImpact && matchesStatus && matchesSearch;
  });

  // Filter insights based on selected filters
  const filteredInsights = productInsights.filter(insight => {
    const matchesTheme = selectedTheme === 'All Themes' || insight.theme === selectedTheme;
    const matchesTags = selectedRisk === 'All Risks' || (insight.tags && insight.tags.some(tag => tag.includes(selectedRisk.replace('All Risks', ''))));
    const matchesSearch = searchTerm === '' || 
                         insight.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         insight.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (insight.tags && insight.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    
    return matchesTheme && matchesTags && matchesSearch;
  });

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'opportunity': return <Lightbulb className="w-5 h-5 text-yellow-500" />;
      case 'concern': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      default: return <Activity className="w-5 h-5 text-blue-500" />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'opportunity': return 'border-yellow-600 bg-yellow-900';
      case 'concern': return 'border-red-600 bg-red-900';
      case 'success': return 'border-green-600 bg-green-900';
      default: return 'border-blue-600 bg-blue-900';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact.toLowerCase()) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Progress': return 'bg-blue-900 text-blue-300 border-blue-600';
      case 'Ready': return 'bg-green-900 text-green-300 border-green-600';
      case 'Discovery': return 'bg-purple-900 text-purple-300 border-purple-600';
      case 'Backlog': return 'bg-gray-900 text-gray-300 border-gray-600';
      case 'Completed': return 'bg-teal-900 text-teal-300 border-teal-600';
      default: return 'bg-gray-900 text-gray-300 border-gray-600';
    }
  };

  const getChangeColor = (change: number) => {
    return change > 0 ? 'text-green-400' : change < 0 ? 'text-red-400' : 'text-gray-400';
  };

  // Function to open AI Co-Pilot with a specific prompt
  const openAIWithPrompt = (prompt: string) => {
    // Dispatch an event that the Layout component listens for
    const event = new CustomEvent('ai-chat-message', { 
      detail: { message: prompt } 
    });
    window.dispatchEvent(event);
    
    // Show a notification that the AI is being prompted
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-indigo-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse';
    notification.textContent = '🤖 AI Co-Pilot activated with context-aware prompt';
    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 3000);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'strategic':
        return (
          <div className="space-y-6">
            {/* Strategic Metrics with Horizontal Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {strategicMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <h4 className="text-white font-medium mb-3">{metric.name}</h4>
                  
                  {/* Horizontal metrics layout */}
                  <div className="grid grid-cols-3 gap-4 mb-3">
                    <div className="text-center">
                      <div className="text-xs text-gray-400">Current</div>
                      <div className="text-lg font-bold text-white">{metric.current}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-400">Target</div>
                      <div className="text-lg font-bold text-gray-300">{metric.target}%</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xs text-gray-400">Forecast</div>
                      <div className="text-lg font-bold text-green-400">{metric.forecast}%</div>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-300" 
                      style={{ 
                        width: `${(metric.current / 100) * 100}%`,
                        backgroundColor: metric.color
                      }}
                    ></div>
                  </div>
                  
                  {/* Theme tag */}
                  {metric.theme && (
                    <div className="mt-2 flex justify-end">
                      <span className="text-xs px-2 py-1 bg-gray-700 text-gray-300 rounded-full">
                        {metric.theme}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Strategic Alignment Progress Over Time */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Target className="w-5 h-5 text-indigo-400" />
                <span>Strategic Alignment Progress (12 Months)</span>
              </h3>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={strategicAlignmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="month" stroke="#9CA3AF" fontSize={10} />
                  <YAxis stroke="#9CA3AF" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Line type="monotone" dataKey="customerAcquisition" stroke="#3B82F6" strokeWidth={2} name="Customer Acquisition" />
                  <Line type="monotone" dataKey="revenueGrowth" stroke="#10B981" strokeWidth={2} name="Revenue Growth" />
                  <Line type="monotone" dataKey="userRetention" stroke="#8B5CF6" strokeWidth={2} name="User Retention" />
                  <Line type="monotone" dataKey="marketExpansion" stroke="#F59E0B" strokeWidth={2} name="Market Expansion" />
                  <Line type="monotone" dataKey="operationalEfficiency" stroke="#EF4444" strokeWidth={2} name="Operational Efficiency" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'prioritization':
        return (
          <div className="space-y-6">
            {/* Filters and Search */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-5 h-5 text-indigo-400" />
                  <h3 className="text-white font-medium">Backlog Filters</h3>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  {/* Theme Filter */}
                  <div className="relative">
                    <select
                      value={selectedTheme}
                      onChange={(e) => setSelectedTheme(e.target.value)}
                      className="bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none text-sm appearance-none pr-8"
                    >
                      {strategicThemes.map((theme) => (
                        <option key={theme} value={theme === 'All Themes' ? 'All Themes' : theme}>
                          {theme}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  </div>
                  
                  {/* Risk Filter */}
                  <div className="relative">
                    <select
                      value={selectedRisk}
                      onChange={(e) => setSelectedRisk(e.target.value)}
                      className="bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none text-sm appearance-none pr-8"
                    >
                      {riskAreas.map((risk) => (
                        <option key={risk} value={risk}>
                          {risk}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  </div>
                  
                  {/* Impact Filter */}
                  <div className="relative">
                    <select
                      value={selectedImpact}
                      onChange={(e) => setSelectedImpact(e.target.value)}
                      className="bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none text-sm appearance-none pr-8"
                    >
                      {impactAreas.map((impact) => (
                        <option key={impact} value={impact}>
                          {impact}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  </div>
                  
                  {/* Status Filter */}
                  <div className="relative">
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="bg-gray-700 text-white px-3 py-2 rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none text-sm appearance-none pr-8"
                    >
                      <option value="all">All Statuses</option>
                      <option value="in progress">In Progress</option>
                      <option value="ready">Ready</option>
                      <option value="discovery">Discovery</option>
                      <option value="backlog">Backlog</option>
                      <option value="completed">Completed</option>
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  </div>
                </div>
                
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search backlog items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none text-sm"
                  />
                </div>
                
                {/* What-If Simulator Button */}
                <button
                  onClick={() => setIsSimulatorOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm"
                >
                  <Sliders className="w-4 h-4" />
                  <span>What-If Simulator</span>
                </button>
              </div>
            </div>
            
            {/* Backlog Health Summary */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Activity className="w-5 h-5 text-indigo-400" />
                <span>Backlog Health Summary</span>
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400">Avg Strategic Alignment</div>
                  <div className="text-xl font-bold text-green-400">
                    {Math.round(filteredPrioritizationFeatures.reduce((sum, item) => sum + item.strategicAlignment, 0) / filteredPrioritizationFeatures.length)}%
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400">Avg Business Value</div>
                  <div className="text-xl font-bold text-blue-400">
                    {Math.round(filteredPrioritizationFeatures.reduce((sum, item) => sum + item.businessValue, 0) / filteredPrioritizationFeatures.length)}
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400">Avg Risk Score</div>
                  <div className="text-xl font-bold text-red-400">
                    {Math.round(filteredPrioritizationFeatures.reduce((sum, item) => sum + item.riskScore, 0) / filteredPrioritizationFeatures.length)}
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400">Avg Learning Velocity</div>
                  <div className="text-xl font-bold text-purple-400">
                    {Math.round(filteredPrioritizationFeatures.reduce((sum, item) => sum + item.learningVelocity, 0) / filteredPrioritizationFeatures.length)}
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400">Avg Cycle Time</div>
                  <div className="text-xl font-bold text-yellow-400">
                    {Math.round(filteredPrioritizationFeatures.reduce((sum, item) => sum + item.cycleTime, 0) / filteredPrioritizationFeatures.length)} days
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-3 text-center">
                  <div className="text-xs text-gray-400">Items</div>
                  <div className="text-xl font-bold text-white">
                    {filteredPrioritizationFeatures.length}
                  </div>
                </div>
              </div>
            </div>

            {/* Prioritization Features with Optimized Layout */}
            <div className="space-y-4">
              {filteredPrioritizationFeatures.length > 0 ? (
                filteredPrioritizationFeatures.map((feature) => (
                  <div key={feature.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    {/* Header Row */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="text-lg font-semibold text-white">{feature.name}</div>
                        <div className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(feature.status)}`}>
                          {feature.status}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-400">Priority Score</div>
                        <div className="text-xl font-bold text-indigo-400">{feature.priority}</div>
                      </div>
                    </div>

                    {/* Metrics Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-3">
                      <div className="text-center">
                        <div className="text-xs text-gray-400">Effort</div>
                        <div className="text-sm font-bold text-white">{feature.effort}w</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-400">Impact</div>
                        <div className={`text-sm font-bold ${getImpactColor(feature.impact.toLowerCase())}`}>
                          {feature.impact}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-400">Confidence</div>
                        <div className="text-sm font-bold text-white">{feature.confidence}%</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-400">Owner</div>
                        <div className="text-sm font-bold text-white">{feature.owner}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-400">Timeline</div>
                        <div className="text-sm font-bold text-white">{feature.timeline}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-400">Strategic Alignment</div>
                        <div className="text-sm font-bold text-green-400">{feature.strategicAlignment}%</div>
                      </div>
                    </div>

                    {/* Progress Bar (for In Progress items) */}
                    {feature.status === 'In Progress' && feature.progress !== undefined && (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>Progress</span>
                          <span>{feature.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full" 
                            style={{ width: `${feature.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* KPI Impact */}
                    <div className="mb-3">
                      <div className="text-xs text-gray-400 mb-1">KPI Impact</div>
                      <div className="flex flex-wrap gap-1">
                        {feature.kpiImpact.slice(0, 3).map((kpi, index) => (
                          <span key={index} className="px-2 py-1 bg-indigo-900 text-indigo-300 rounded-full text-xs">
                            {kpi}
                          </span>
                        ))}
                        {feature.kpiImpact.length > 3 && (
                          <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
                            +{feature.kpiImpact.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Dependencies and Risks Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Dependencies</div>
                        <div className="flex flex-wrap gap-1">
                          {feature.dependencies.map((dep, index) => (
                            <span key={index} className="px-2 py-1 bg-yellow-900 text-yellow-300 rounded-full text-xs">
                              {dep}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 mb-1">Risks</div>
                        <div className="flex flex-wrap gap-1">
                          {feature.risks.map((risk, index) => (
                            <span key={index} className="px-2 py-1 bg-red-900 text-red-300 rounded-full text-xs">
                              {risk}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Theme and Tags */}
                    <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-700">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-400">Theme:</span>
                        <span className="px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
                          {feature.theme}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="text-indigo-400 hover:text-indigo-300 text-sm">
                          View Details
                        </button>
                        <button 
                          onClick={() => openAIWithPrompt(`Analyze the strategic impact and risks of the "${feature.name}" feature with priority score ${feature.priority}. Consider its business value of ${feature.businessValue}, risk score of ${feature.riskScore}, and strategic alignment of ${feature.strategicAlignment}. Provide recommendations for optimizing its delivery.`)}
                          className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
                        >
                          <Brain className="w-3 h-3" />
                          <span>AI Analysis</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-gray-800 rounded-lg p-8 border border-gray-700 text-center">
                  <Target className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No items match your filters</h3>
                  <p className="text-gray-400 mb-4">Try adjusting your search criteria or filters</p>
                  <button 
                    onClick={() => {
                      setSelectedTheme('All Themes');
                      setSelectedRisk('All Risks');
                      setSelectedImpact('All Impact Areas');
                      setSelectedStatus('all');
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                  >
                    Reset Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        );

      case 'delivery':
        return (
          <div className="space-y-6">
            {/* Learning Velocity Trend */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Brain className="w-5 h-5 text-purple-400" />
                <span>Learning Velocity Trend</span>
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={learningVelocityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="week" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Line type="monotone" dataKey="velocity" stroke="#8B5CF6" strokeWidth={3} />
                  <Line type="monotone" dataKey="discoveries" stroke="#10B981" strokeWidth={2} strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="validations" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Delivery Timeline */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                <span>Delivery Timeline</span>
              </h3>
              
              <div className="relative">
                {/* Timeline header */}
                <div className="flex mb-4 pl-40">
                  {['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov'].map((month, i) => (
                    <div key={i} className="flex-1 text-center text-sm text-gray-400">
                      {month}
                    </div>
                  ))}
                </div>
                
                {/* Timeline items */}
                <div className="space-y-6">
                  {filteredPrioritizationFeatures
                    .filter(feature => feature.startDate && feature.endDate)
                    .map((feature) => {
                      // Calculate position and width based on dates
                      const startDate = new Date(feature.startDate!);
                      const endDate = new Date(feature.endDate!);
                      const today = new Date();
                      
                      // For demo purposes, we'll use a fixed scale
                      // In a real implementation, this would be calculated based on the date range
                      const startMonth = startDate.getMonth();
                      const endMonth = endDate.getMonth();
                      const startPosition = (startMonth - 5) * 16.66; // Assuming June (month 5) is the start
                      const duration = (endMonth - startMonth + 1) * 16.66;
                      
                      return (
                        <div key={feature.id} className="flex items-center">
                          <div className="w-40 pr-4 text-right">
                            <div className="text-sm font-medium text-white">{feature.name}</div>
                            <div className="text-xs text-gray-400">{feature.owner}</div>
                          </div>
                          <div className="flex-1 h-8 relative">
                            <div className="absolute inset-y-0 bg-gray-700 w-full"></div>
                            <div 
                              className={`absolute inset-y-0 rounded ${
                                feature.status === 'In Progress' ? 'bg-blue-900 border border-blue-700' : 
                                feature.status === 'Completed' ? 'bg-green-900 border border-green-700' :
                                'bg-gray-600 border border-gray-500'
                              }`}
                              style={{ 
                                left: `${Math.max(0, startPosition)}%`, 
                                width: `${Math.min(100, duration)}%` 
                              }}
                            >
                              <div className="flex items-center justify-center h-full">
                                <span className="text-xs text-white truncate px-2">
                                  {feature.status === 'In Progress' ? `${feature.progress}%` : feature.status}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* Hypotheses Pipeline */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Lightbulb className="w-5 h-5 text-yellow-400" />
                <span>Hypotheses Pipeline</span>
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={hypothesesPipelineData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="stage" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Bar dataKey="count" fill="#4F46E5" />
                </BarChart>
              </ResponsiveContainer>
              
              {/* Pipeline Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
                {hypothesesPipelineData.map((stage, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-3 text-center">
                    <div className="text-xs text-gray-400">{stage.stage}</div>
                    <div className="text-lg font-bold text-white">{stage.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'impact':
        return (
          <div className="space-y-6">
            {/* Behavior Change Analysis */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Users className="w-5 h-5 text-blue-400" />
                <span>Customer Behavior Analysis</span>
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={behaviorChangeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="segment" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#F9FAFB'
                    }}
                  />
                  <Bar dataKey="adoption" fill="#3B82F6" name="Adoption Rate" />
                  <Bar dataKey="retention" fill="#10B981" name="Retention Rate" />
                  <Bar dataKey="satisfaction" fill="#F59E0B" name="Satisfaction Score" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Feature Adoption */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Zap className="w-5 h-5 text-indigo-400" />
                <span>Feature Adoption Rates</span>
              </h3>
              <div className="space-y-4">
                {featureAdoptionData.map((feature, index) => (
                  <div key={index} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-medium">{feature.feature}</h4>
                      <div className="flex items-center space-x-2">
                        <span className={`text-sm ${getImpactColor(feature.impact)} capitalize`}>
                          {feature.impact} impact
                        </span>
                        <span className="text-sm text-gray-400">{feature.users.toLocaleString()} users</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 bg-gray-600 rounded-full h-3">
                        <div 
                          className="bg-indigo-500 h-3 rounded-full transition-all duration-300" 
                          style={{ width: `${feature.adoption}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-white">{feature.adoption}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Business Impact Analysis */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                <span>Business Impact Analysis</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ROI by Feature */}
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">ROI by Feature</h4>
                  <div className="space-y-3">
                    {filteredPrioritizationFeatures.slice(0, 4).map((feature) => (
                      <div key={feature.id} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                          <span className="text-sm text-gray-300">{feature.name}</span>
                        </div>
                        <div className="text-sm font-medium text-green-400">
                          {Math.round(feature.businessValue / feature.effort * 10)}x
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Risk vs Value */}
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Risk vs Value Matrix</h4>
                  <div className="h-48 relative">
                    <div className="absolute inset-0 grid grid-cols-3 grid-rows-3 gap-0.5">
                      <div className="bg-red-900 bg-opacity-20 border border-red-800 border-opacity-20"></div>
                      <div className="bg-yellow-900 bg-opacity-20 border border-yellow-800 border-opacity-20"></div>
                      <div className="bg-green-900 bg-opacity-20 border border-green-800 border-opacity-20"></div>
                      <div className="bg-red-900 bg-opacity-30 border border-red-800 border-opacity-30"></div>
                      <div className="bg-yellow-900 bg-opacity-30 border border-yellow-800 border-opacity-30"></div>
                      <div className="bg-green-900 bg-opacity-30 border border-green-800 border-opacity-30"></div>
                      <div className="bg-red-900 bg-opacity-40 border border-red-800 border-opacity-40"></div>
                      <div className="bg-yellow-900 bg-opacity-40 border border-yellow-800 border-opacity-40"></div>
                      <div className="bg-green-900 bg-opacity-40 border border-green-800 border-opacity-40"></div>
                    </div>
                    
                    {/* Plot points for features */}
                    {filteredPrioritizationFeatures.slice(0, 5).map((feature) => {
                      // Calculate position based on risk and value
                      const x = 100 - (feature.riskScore); // Invert risk score for x-axis
                      const y = 100 - (feature.businessValue); // Invert business value for y-axis
                      
                      return (
                        <div 
                          key={feature.id}
                          className="absolute w-6 h-6 rounded-full bg-indigo-600 border-2 border-indigo-400 flex items-center justify-center transform -translate-x-1/2 -translate-y-1/2 cursor-help"
                          style={{ 
                            left: `${x}%`, 
                            top: `${y}%` 
                          }}
                          title={`${feature.name}: Value ${feature.businessValue}, Risk ${feature.riskScore}`}
                        >
                          <span className="text-xs text-white font-bold">{feature.id}</span>
                        </div>
                      );
                    })}
                    
                    {/* Axis labels */}
                    <div className="absolute bottom-0 left-0 text-xs text-gray-400">High Risk</div>
                    <div className="absolute bottom-0 right-0 text-xs text-gray-400">Low Risk</div>
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 text-xs text-gray-400">High Value</div>
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-xs text-gray-400">Low Value</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'insights':
        return (
          <div className="space-y-6">
            {/* Product Insights */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Activity className="w-5 h-5 text-green-400" />
                  <h3 className="text-lg font-semibold text-white">AI-Generated Product Insights</h3>
                </div>
                <button 
                  onClick={() => openAIWithPrompt("Analyze our product metrics and provide strategic recommendations for improving our key performance indicators. Focus on opportunities for growth and potential risks we should mitigate.")}
                  className="flex items-center space-x-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors"
                >
                  <Brain className="w-4 h-4" />
                  <span>Generate New Insights</span>
                </button>
              </div>
              
              <div className="space-y-4">
                {filteredInsights.length > 0 ? (
                  filteredInsights.map((insight) => (
                    <div 
                      key={insight.id} 
                      className={`p-4 rounded-lg border ${getInsightColor(insight.type)} cursor-pointer hover:bg-opacity-80 transition-colors`}
                      onClick={() => setSelectedInsight(insight)}
                    >
                      <div className="flex items-start space-x-3">
                        {getInsightIcon(insight.type)}
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-white font-medium">{insight.title}</h4>
                            <span className="text-xs text-gray-400">{insight.timeframe}</span>
                          </div>
                          <p className="text-sm text-gray-300 mb-3">{insight.description}</p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <span className={`text-xs ${getImpactColor(insight.impact)} capitalize`}>
                                {insight.impact} impact
                              </span>
                              <span className="text-xs text-gray-400">
                                {insight.confidence}% confidence
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAIWithPrompt(`Analyze the "${insight.title}" insight in detail. The insight states: "${insight.description}". Provide a deeper analysis of its implications, potential actions we should take, and how it relates to our strategic goals.`);
                                }}
                                className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours flex items-center space-x-1"
                              >
                                <Brain className="w-3 h-3" />
                                <span>AI Analysis</span>
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // This would typically trigger an action to implement the recommendation
                                  alert(`Action triggered: ${insight.action}`);
                                }}
                                className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours"
                              >
                                {insight.action}
                              </button>
                            </div>
                          </div>
                          
                          {/* Related Items and Tags */}
                          {(insight.relatedItems?.length || insight.tags?.length) && (
                            <div className="mt-3 pt-2 border-t border-gray-700 flex flex-wrap items-center gap-2">
                              {insight.relatedItems?.map((itemId) => {
                                const relatedItem = prioritizationFeatures.find(f => f.id === itemId);
                                return relatedItem ? (
                                  <span key={itemId} className="px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs flex items-center space-x-1">
                                    <Target className="w-3 h-3 text-indigo-400" />
                                    <span>{relatedItem.name.length > 15 ? relatedItem.name.substring(0, 15) + '...' : relatedItem.name}</span>
                                  </span>
                                ) : null;
                              })}
                              
                              {insight.tags?.map((tag, index) => (
                                <span key={index} className="px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <Lightbulb className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">No insights match your filters</h3>
                    <p className="text-gray-400 mb-4">Try adjusting your search criteria or filters</p>
                    <button 
                      onClick={() => {
                        setSelectedTheme('All Themes');
                        setSelectedRisk('All Risks');
                        setSearchTerm('');
                      }}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                    >
                      Reset Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* AI Recommendations */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Brain className="w-5 h-5 text-purple-400" />
                <span>AI Strategic Recommendations</span>
              </h3>
              
              <div className="space-y-4">
                <div className="bg-indigo-900 bg-opacity-50 border border-indigo-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Brain className="w-5 h-5 text-indigo-400 mt-1" />
                    <div>
                      <h4 className="text-white font-medium mb-2">Prioritize AI Personalisation Engine</h4>
                      <p className="text-gray-300 text-sm mb-3">
                        Based on current metrics and market signals, the AI Personalisation Engine shows the highest potential ROI (340%) and strategic alignment (98%). Recommend accelerating this initiative and allocating additional resources to ensure timely delivery.
                      </p>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => openAIWithPrompt("Provide a detailed analysis of why we should prioritize the AI Personalisation Engine. Include potential ROI calculations, resource requirements, and implementation timeline recommendations.")}
                          className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours flex items-center space-x-1"
                        >
                          <MessageSquare className="w-3 h-3" />
                          <span>Tell me more</span>
                        </button>
                        <button className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours">
                          Apply recommendation
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-indigo-900 bg-opacity-50 border border-indigo-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Brain className="w-5 h-5 text-indigo-400 mt-1" />
                    <div>
                      <h4 className="text-white font-medium mb-2">Address Feature Adoption Plateau</h4>
                      <p className="text-gray-300 text-sm mb-3">
                        Feature adoption has plateaued in the enterprise segment (concern detected). Recommend conducting targeted user research to identify barriers and implementing a focused enablement campaign to drive adoption. This could improve overall adoption metrics by an estimated 15-20%.
                      </p>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => openAIWithPrompt("Analyze the feature adoption plateau in our enterprise segment. What are the likely causes and what specific actions should we take to address this issue?")}
                          className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours flex items-center space-x-1"
                        >
                          <MessageSquare className="w-3 h-3" />
                          <span>Tell me more</span>
                        </button>
                        <button className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours">
                          Apply recommendation
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-indigo-900 bg-opacity-50 border border-indigo-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Brain className="w-5 h-5 text-indigo-400 mt-1" />
                    <div>
                      <h4 className="text-white font-medium mb-2">Optimize Resource Allocation</h4>
                      <p className="text-gray-300 text-sm mb-3">
                        Current resource allocation shows potential inefficiencies. Recommend shifting 20% of resources from lower-priority items to the Mobile Checkout Optimization and AI Personalisation initiatives. This reallocation could improve overall delivery efficiency by an estimated 15%.
                      </p>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => openAIWithPrompt("Provide a detailed resource optimization plan for our current backlog. Specifically, how should we reallocate resources to maximize strategic impact while minimizing delivery risk?")}
                          className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours flex items-center space-x-1"
                        >
                          <MessageSquare className="w-3 h-3" />
                          <span>Tell me more</span>
                        </button>
                        <button 
                          onClick={() => setIsSimulatorOpen(true)}
                          className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours"
                        >
                          Simulate this change
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {productMetrics.map((metric) => (
                <div 
                  key={metric.id} 
                  className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-gray-600 transition-colours cursor-pointer"
                  onClick={() => setSelectedMetric(metric)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-300">{metric.name}</h3>
                    {metric.positive ? (
                      <TrendingUp className="w-5 h-5 text-green-500" />
                    ) : (
                      <TrendingUp className="w-5 h-5 text-red-500 transform rotate-180" />
                    )}
                  </div>
                  <div className="flex items-baseline space-x-2 mb-2">
                    <span className="text-2xl font-bold text-white">{metric.value}</span>
                    <span className="text-sm text-gray-400">{metric.unit}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${metric.positive ? 'text-green-500' : 'text-red-500'}`}>
                      {metric.positive ? '+' : ''}{metric.change}%
                    </span>
                    <span className="text-xs text-gray-500">Target: {metric.target}{metric.unit}</span>
                  </div>
                  <div className="mt-3 w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${metric.value >= metric.target ? 'bg-green-500' : 'bg-yellow-500'}`}
                      style={{ width: `${Math.min((metric.value / metric.target) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Strategic Overview */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <Target className="w-5 h-5 text-indigo-400" />
                <span>Strategic Overview</span>
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Strategic Alignment */}
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Strategic Alignment</h4>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="text-3xl font-bold text-indigo-400">92%</div>
                    <div className="text-sm text-green-400">+4% vs last quarter</div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>Customer Acquisition</span>
                        <span>92%</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>Revenue Growth</span>
                        <span>89%</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '89%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>User Retention</span>
                        <span>96%</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div className="bg-purple-500 h-2 rounded-full" style={{ width: '96%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Delivery Health */}
                <div className="bg-gray-700 rounded-lg p-4">
                  <h4 className="text-white font-medium mb-3">Delivery Health</h4>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="text-3xl font-bold text-green-400">85%</div>
                    <div className="text-sm text-green-400">+7% vs last quarter</div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>On-Time Delivery</span>
                        <span>88%</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '88%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>Quality Score</span>
                        <span>92%</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '92%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs text-gray-400 mb-1">
                        <span>Resource Utilization</span>
                        <span>78%</span>
                      </div>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '78%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Top Priorities */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  <span>Top Priorities</span>
                </h3>
                <button 
                  onClick={() => setActiveTab('prioritization')}
                  className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  View All →
                </button>
              </div>
              
              <div className="space-y-3">
                {prioritizationFeatures.slice(0, 3).map((feature) => (
                  <div key={feature.id} className="bg-gray-700 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(feature.status)}`}>
                          {feature.status}
                        </div>
                        <h4 className="text-white font-medium">{feature.name}</h4>
                      </div>
                      <div className="text-sm font-bold text-indigo-400">{feature.priority}</div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <span>Owner: {feature.owner}</span>
                      <span>Timeline: {feature.timeline}</span>
                      <span className={`${getImpactColor(feature.impact.toLowerCase())}`}>
                        {feature.impact} Impact
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Key Insights */}
            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center space-x-2">
                  <Lightbulb className="w-5 h-5 text-yellow-400" />
                  <span>Key Insights</span>
                </h3>
                <button 
                  onClick={() => setActiveTab('insights')}
                  className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors"
                >
                  View All →
                </button>
              </div>
              
              <div className="space-y-3">
                {productInsights.slice(0, 3).map((insight) => (
                  <div 
                    key={insight.id} 
                    className={`p-3 rounded-lg border ${getInsightColor(insight.type)} cursor-pointer hover:bg-opacity-80 transition-colors`}
                    onClick={() => setSelectedInsight(insight)}
                  >
                    <div className="flex items-start space-x-3">
                      {getInsightIcon(insight.type)}
                      <div>
                        <h4 className="text-white font-medium mb-1">{insight.title}</h4>
                        <p className="text-sm text-gray-300">{insight.description}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs ${getImpactColor(insight.impact)}`}>
                            {insight.impact} impact
                          </span>
                          <span className="text-xs text-gray-400">
                            {insight.timeframe}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3">
            <Target className="w-8 h-8 text-blue-400" />
            <span>Product Strategy Dashboard</span>
          </h1>
          <p className="text-gray-400">Product performance, innovation metrics, and customer insights</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-indigo-500 focus:outline-none"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-300">Live Product Data</span>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-700">
        <nav className="flex space-x-8 overflow-x-auto">
          {[
            { id: 'overview', name: 'Overview', icon: BarChart3 },
            { id: 'strategic', name: 'Strategic Alignment', icon: Target },
            { id: 'prioritization', name: 'Prioritization', icon: Star },
            { id: 'delivery', name: 'Delivery & Learning', icon: Clock },
            { id: 'impact', name: 'Impact & Outcomes', icon: TrendingUp },
            { id: 'insights', name: 'Insights & Alerts', icon: Lightbulb }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Metric Detail Modal */}
      {selectedMetric && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h3 className="text-xl font-bold text-white">{selectedMetric.name}</h3>
              <button
                onClick={() => setSelectedMetric(null)}
                className="text-gray-400 hover:text-white transition-colours"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400">Current Value</div>
                  <div className="text-2xl font-bold text-white">
                    {selectedMetric.value}{selectedMetric.unit}
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400">Target</div>
                  <div className="text-2xl font-bold text-white">
                    {selectedMetric.target}{selectedMetric.unit}
                  </div>
                </div>
              </div>
              <p className="text-gray-300 mb-4">{selectedMetric.description}</p>
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${selectedMetric.positive ? 'text-green-500' : 'text-red-500'}`}>
                  {selectedMetric.positive ? '+' : ''}{selectedMetric.change}% vs last period
                </span>
                {selectedMetric.positive ? (
                  <ArrowUp className="w-4 h-4 text-green-500" />
                ) : (
                  <ArrowDown className="w-4 h-4 text-red-500" />
                )}
              </div>
              
              <div className="mt-6">
                <h4 className="text-white font-medium mb-3">Trend Analysis</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={[
                    { month: 'Jan', value: selectedMetric.value * 0.85 },
                    { month: 'Feb', value: selectedMetric.value * 0.88 },
                    { month: 'Mar', value: selectedMetric.value * 0.92 },
                    { month: 'Apr', value: selectedMetric.value * 0.95 },
                    { month: 'May', value: selectedMetric.value * 0.97 },
                    { month: 'Jun', value: selectedMetric.value }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="month" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1F2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#F9FAFB'
                      }}
                    />
                    <Line type="monotone" dataKey="value" stroke="#4F46E5" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              <div className="mt-6">
                <h4 className="text-white font-medium mb-3">AI Analysis</h4>
                <div className="bg-indigo-900 bg-opacity-50 border border-indigo-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Brain className="w-5 h-5 text-indigo-400 mt-1" />
                    <div>
                      <p className="text-gray-300 text-sm">
                        {selectedMetric.positive 
                          ? `The ${selectedMetric.name.toLowerCase()} is performing well, exceeding the target by ${(selectedMetric.value - selectedMetric.target).toFixed(1)}${selectedMetric.unit}. This positive trend indicates that our strategic initiatives in this area are effective. To maintain this momentum, consider further investment in related features and capabilities.`
                          : `The ${selectedMetric.name.toLowerCase()} is currently below target by ${(selectedMetric.target - selectedMetric.value).toFixed(1)}${selectedMetric.unit}. This suggests that our current approach may need adjustment. Consider conducting user research to identify barriers and implementing targeted improvements to address the gap.`
                        }
                      </p>
                      <button 
                        onClick={() => openAIWithPrompt(`Provide a detailed analysis of our ${selectedMetric.name.toLowerCase()} metric which is currently at ${selectedMetric.value}${selectedMetric.unit} compared to a target of ${selectedMetric.target}${selectedMetric.unit}. What are the key drivers of this performance, and what specific actions should we take to improve it further?`)}
                        className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours mt-2 flex items-center space-x-1"
                      >
                        <MessageSquare className="w-3 h-3" />
                        <span>Get deeper analysis</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Insight Detail Modal */}
      {selectedInsight && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div className="flex items-center space-x-3">
                {getInsightIcon(selectedInsight.type)}
                <h3 className="text-xl font-bold text-white">{selectedInsight.title}</h3>
              </div>
              <button
                onClick={() => setSelectedInsight(null)}
                className="text-gray-400 hover:text-white transition-colours"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400">Impact</div>
                  <div className={`text-xl font-bold capitalize ${getImpactColor(selectedInsight.impact)}`}>
                    {selectedInsight.impact}
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400">Confidence</div>
                  <div className="text-xl font-bold text-white">
                    {selectedInsight.confidence}%
                  </div>
                </div>
                <div className="bg-gray-700 rounded-lg p-4 text-center">
                  <div className="text-sm text-gray-400">Timeframe</div>
                  <div className="text-xl font-bold text-white">
                    {selectedInsight.timeframe}
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="text-white font-medium mb-3">Description</h4>
                <p className="text-gray-300">{selectedInsight.description}</p>
              </div>
              
              {/* Related Items */}
              {selectedInsight.relatedItems && selectedInsight.relatedItems.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-white font-medium mb-3">Related Backlog Items</h4>
                  <div className="space-y-3">
                    {selectedInsight.relatedItems.map((itemId) => {
                      const relatedItem = prioritizationFeatures.find(f => f.id === itemId);
                      return relatedItem ? (
                        <div key={itemId} className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(relatedItem.status)}`}>
                                {relatedItem.status}
                              </div>
                              <h5 className="text-white font-medium">{relatedItem.name}</h5>
                            </div>
                            <div className="text-sm font-bold text-indigo-400">{relatedItem.priority}</div>
                          </div>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              
              {/* AI Analysis */}
              <div className="mb-6">
                <h4 className="text-white font-medium mb-3">AI Analysis</h4>
                <div className="bg-indigo-900 bg-opacity-50 border border-indigo-700 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Brain className="w-5 h-5 text-indigo-400 mt-1" />
                    <div>
                      <p className="text-gray-300 text-sm">
                        {selectedInsight.type === 'opportunity' 
                          ? `This opportunity represents a significant potential for business growth and competitive advantage. With a ${selectedInsight.confidence}% confidence level and ${selectedInsight.impact} impact rating, it should be prioritized in the product roadmap. The data suggests that acting on this insight could drive substantial improvements in key metrics.`
                          : selectedInsight.type === 'concern'
                          ? `This concern requires attention to prevent potential negative impacts on business performance. With a ${selectedInsight.confidence}% confidence level, the data strongly suggests this is a valid issue. Addressing it promptly will help mitigate risks and maintain strategic momentum.`
                          : `This success represents a validated approach that has delivered measurable results. With a ${selectedInsight.confidence}% confidence level, we can be certain that the positive outcomes are directly attributable to our actions. Consider how to apply these learnings to other areas of the product.`
                        }
                      </p>
                      <div className="flex items-center space-x-3 mt-3">
                        <button 
                          onClick={() => openAIWithPrompt(`Provide a comprehensive analysis of the "${selectedInsight.title}" insight. The insight states: "${selectedInsight.description}". It has a ${selectedInsight.impact} impact and ${selectedInsight.confidence}% confidence level. What are the strategic implications, potential actions, and expected outcomes if we ${selectedInsight.action.toLowerCase()}?`)}
                          className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours flex items-center space-x-1"
                        >
                          <MessageSquare className="w-3 h-3" />
                          <span>Get deeper analysis</span>
                        </button>
                        <button className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours">
                          {selectedInsight.action}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Tags */}
              {selectedInsight.tags && selectedInsight.tags.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedInsight.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-700 text-gray-300 rounded-full text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-6 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                Theme: {selectedInsight.theme || 'Unclassified'}
              </div>
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => {
                    setSelectedInsight(null);
                    // This would typically trigger an action to create a backlog item
                    alert('Create backlog item from insight');
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                >
                  Create Backlog Item
                </button>
                <button 
                  onClick={() => {
                    setSelectedInsight(null);
                    // This would typically trigger an action to create an experiment
                    alert('Create experiment from insight');
                  }}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
                >
                  Create Experiment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* What-If Simulator Modal */}
      {isSimulatorOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <div className="flex items-center space-x-3">
                <Sliders className="w-6 h-6 text-indigo-400" />
                <h3 className="text-xl font-bold text-white">What-If Scenario Simulator</h3>
              </div>
              <button
                onClick={() => setIsSimulatorOpen(false)}
                className="text-gray-400 hover:text-white transition-colours"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Scenario Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Scenario
                </label>
                <select
                  value={currentScenario?.id || ''}
                  onChange={(e) => {
                    const scenarioId = parseInt(e.target.value);
                    const scenario = simulationScenarios.find(s => s.id === scenarioId);
                    setCurrentScenario(scenario || null);
                  }}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                >
                  <option value="">Select a scenario</option>
                  {simulationScenarios.map((scenario) => (
                    <option key={scenario.id} value={scenario.id}>
                      {scenario.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {currentScenario ? (
                <>
                  <div className="mb-6">
                    <h4 className="text-white font-medium mb-3">{currentScenario.description}</h4>
                    
                    {/* Parameters */}
                    <div className="space-y-4 mb-6">
                      <h5 className="text-sm font-medium text-gray-300">Adjust Parameters</h5>
                      {simulationParameters.map((param, index) => (
                        <div key={index} className="bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <div className="text-white font-medium">{param.name}</div>
                              <div className="text-xs text-gray-400">{param.description}</div>
                            </div>
                            <div className="text-lg font-bold text-indigo-400">
                              {param.value}{param.unit}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button 
                              onClick={() => updateParameter(index, Math.max(param.min, param.value - param.step))}
                              className="p-1 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                              disabled={param.value <= param.min}
                            >
                              <MinusCircle className="w-4 h-4" />
                            </button>
                            <div className="flex-1">
                              <input
                                type="range"
                                min={param.min}
                                max={param.max}
                                step={param.step}
                                value={param.value}
                                onChange={(e) => updateParameter(index, parseFloat(e.target.value))}
                                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                              />
                            </div>
                            <button 
                              onClick={() => updateParameter(index, Math.min(param.max, param.value + param.step))}
                              className="p-1 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors"
                              disabled={param.value >= param.max}
                            >
                              <PlusCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Results */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-medium text-gray-300">Simulation Results</h5>
                        <button
                          onClick={runSimulation}
                          className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm flex items-center space-x-1"
                        >
                          <Play className="w-3 h-3" />
                          <span>Run Simulation</span>
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {currentScenario.results.map((result, index) => (
                          <div key={index} className="bg-gray-700 rounded-lg p-4">
                            <div className="text-white font-medium mb-2">{result.metric}</div>
                            <div className="grid grid-cols-3 gap-2">
                              <div className="text-center">
                                <div className="text-xs text-gray-400">Baseline</div>
                                <div className="text-sm font-bold text-white">{result.baseline}{result.unit}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-xs text-gray-400">Simulated</div>
                                <div className="text-sm font-bold text-indigo-400">{result.simulated}{result.unit}</div>
                              </div>
                              <div className="text-center">
                                <div className="text-xs text-gray-400">Change</div>
                                <div className={`text-sm font-bold ${getChangeColor(result.change)}`}>
                                  {result.change > 0 ? '+' : ''}{result.change}%
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* AI Analysis */}
                    <div>
                      <h5 className="text-sm font-medium text-gray-300 mb-3">AI Analysis</h5>
                      <div className="bg-indigo-900 bg-opacity-50 border border-indigo-700 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                          <Brain className="w-5 h-5 text-indigo-400 mt-1" />
                          <div>
                            <p className="text-gray-300 text-sm">
                              Based on the simulated parameters, this scenario would likely result in 
                              {currentScenario.results.some(r => r.change > 0) ? ' positive' : ' mixed'} outcomes. 
                              {currentScenario.results.find(r => Math.abs(r.change) === Math.max(...currentScenario.results.map(r => Math.abs(r.change))))?.change > 0 
                                ? ' The most significant improvement would be in ' 
                                : ' The most significant impact would be on '}
                              {currentScenario.results.find(r => Math.abs(r.change) === Math.max(...currentScenario.results.map(r => Math.abs(r.change))))?.metric.toLowerCase()}.
                              {currentScenario.results.some(r => r.change < 0) 
                                ? ' However, there are some trade-offs to consider, particularly in ' + 
                                  currentScenario.results.filter(r => r.change < 0).map(r => r.metric.toLowerCase()).join(' and ') + '.'
                                : ' All metrics show improvement, suggesting this could be a beneficial change to implement.'
                              }
                            </p>
                            <button 
                              onClick={() => openAIWithPrompt(`Analyze the following what-if scenario for our product strategy: ${currentScenario.name} - ${currentScenario.description}. The simulation shows the following results: ${currentScenario.results.map(r => `${r.metric}: ${r.baseline}${r.unit} baseline vs ${r.simulated}${r.unit} simulated (${r.change > 0 ? '+' : ''}${r.change}%)`).join(', ')}. What strategic recommendations would you make based on these results?`)}
                              className="text-xs text-indigo-400 hover:text-indigo-300 transition-colours mt-2 flex items-center space-x-1"
                            >
                              <MessageSquare className="w-3 h-3" />
                              <span>Get strategic recommendations</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <Sliders className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">Select a scenario to simulate</h3>
                  <p className="text-gray-400 mb-4">Choose from predefined scenarios or create your own to see how different factors might impact your product strategy</p>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between p-6 border-t border-gray-700">
              <div className="flex items-center space-x-2">
                <HelpCircle className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-400">Simulations are based on historical data and predictive models</span>
              </div>
              <div className="flex items-center space-x-3">
                <button 
                  onClick={() => setIsSimulatorOpen(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
                {currentScenario && (
                  <div className="flex items-center space-x-2">
                    <button className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center space-x-1">
                      <Download className="w-4 h-4" />
                      <span>Export Results</span>
                    </button>
                    <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center space-x-1">
                      <Share2 className="w-4 h-4" />
                      <span>Share Scenario</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDashboard;